import { useEffect, useRef, useState } from 'react';

import { Icon } from '@/components/ui/Icon';
import type { RunSummaryDTO } from '@/api/endpoints';
import { arc } from './ThroatRings';
import { BEAT_S, RING_CYCLE_S } from './CaveEnterCinematic';

/**
 * The walk out, and what you came out with.
 *
 * `CaveEnterCinematic` covers `POST /cave/enter` and makes the descent start
 * somewhere. This is the other end, and until now there was nothing there: tapping
 * SURFACE unmounted the cave and dropped the player on the menu mid-frame. The run
 * simply stopped existing.
 *
 * Worse, it stopped existing *silently*. `/cave/exit` has returned depth, best depth,
 * rocks broken, coins, Kyat and duration since the run engine was written; the client
 * declared four of those fields and the store discarded all six. The one moment a
 * session has a natural ending was the one moment the game said nothing about it.
 *
 * ## Why it is a different shot and not the entrance reversed
 *
 * Playing the walk-in backwards would be cheap and wrong. Both directions are *walking
 * forward* — you leave a mine by going up the shaft, not by reversing down it — so the
 * rings still grow past the camera exactly as they do on the way in. What changes is
 * where the light is:
 *
 * - **Entering**, the light is behind you and shrinking, and the far end is dark.
 * - **Leaving**, the far end is daylight and it grows until it is the whole frame.
 *
 * So the shared machinery is the corridor, and the thing that carries the meaning is
 * a single expanding disc of daylight. The torch loses its job over the shot: at the
 * mouth the warm lantern glow is gone and the miner is a silhouette against the day,
 * which is the one moment in the game the player sees their character from outside.
 *
 * ## The timing is honest, same as the entrance
 *
 * It plays while `POST /cave/exit` is in flight and holds only long enough to read as
 * a shot (`OUT_BEAT_MS`). On a slow server the daylight simply keeps growing; on a
 * fast one the summary arrives as the frame whites out. A player is never made to
 * wait for a cinematic to finish being cinematic.
 *
 * The summary card that follows does **not** auto-dismiss. Every other card in this
 * game does, because they are beats inside a run; this one ends the session, and a
 * haul that vanishes before it is read is the same silence with extra steps.
 */

/** Long enough to read as a shot, short enough not to tax a fast connection. */
export const OUT_BEAT_MS = 1500;
/** The white-out into the summary. */
export const BLOOM_MS = 520;

/** How much longer to hold once the server has answered. Only ever pads a short wait. */
export function holdMs(elapsedMs: number): number {
  return Math.max(0, OUT_BEAT_MS - elapsedMs);
}

/** Seconds, as a run summary should read it: `4m 12s`, never `252`. */
export function readableDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const RING_COUNT = 6;

export function CaveExitCinematic({ summary, ready, onDone }: {
  /** The finished run, or null while `/cave/exit` is still in flight. */
  summary: RunSummaryDTO | null;
  /** The exit call has settled — successfully or not. */
  ready: boolean;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<'walking' | 'bloom' | 'summary'>('walking');
  const startedAt = useRef(Date.now());
  /* Held in a ref for the same reason the entrance does it: an inline arrow at the
     call site changes identity every render, which as a dependency would re-arm the
     handoff timer forever. */
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  /*
   * Two effects, one transition each, and that split is load-bearing.
   *
   * The first draft armed both timers in one effect — the second inside the first
   * one's callback, the way `CaveEnterCinematic` does it. That works there because
   * its effect depends on `[ready]` alone, which does not change mid-flight. Here the
   * effect also depended on `phase`, so the instant the first timer called
   * `setPhase('bloom')` React re-ran the effect, its cleanup fired, and it cleared the
   * summary timer that the same callback had just armed. The shot whited out and
   * stopped there — daylight forever, no haul, and no error anywhere to explain it.
   *
   * Split, neither timer can be cancelled by the other's dependency changing.
   */
  useEffect(() => {
    if (!ready) return;
    const t = window.setTimeout(
      () => setPhase((p) => (p === 'walking' ? 'bloom' : p)),
      holdMs(Date.now() - startedAt.current),
    );
    return () => window.clearTimeout(t);
  }, [ready]);

  useEffect(() => {
    if (phase !== 'bloom') return;
    const t = window.setTimeout(() => setPhase('summary'), BLOOM_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  /* A run with no summary — the exit call failed, or an older server. There is
     nothing to show and nothing to read, so the shot hands straight over rather than
     presenting an empty card. */
  useEffect(() => {
    if (phase === 'summary' && !summary) onDoneRef.current();
  }, [phase, summary]);

  const day = phase === 'walking' ? 1 : 2.6;

  return (
    <div
      className="absolute inset-0 z-40 overflow-hidden bg-[var(--cave-void)] select-none"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Returning to the surface</span>

      {/* Co-located for the same reason the entrance's are: this can render before app
          CSS has settled, and a transition that needs a stylesheet to animate is not a
          transition. */}
      <style>{`
        @keyframes swtOutRing { from { transform: scale(.16); opacity: 0 }
                                14%  { opacity: .85 }
                                66%  { opacity: .45 }
                                to   { transform: scale(2.8); opacity: 0 } }
        @keyframes swtOutWalk { 0%,100% { transform: translate3d(-50%,0,0) }
                                50%     { transform: translate3d(-50%,-1.8%,0) } }
        @keyframes swtOutDay  { 0%,100% { opacity: .88 } 50% { opacity: 1 } }
        @keyframes swtCard    { from { opacity: 0; transform: translateY(10px) }
                                to   { opacity: 1; transform: none } }
        .swt-out-ring { transform-box: view-box; transform-origin: 50% 46%; }
        @media (prefers-reduced-motion: reduce) {
          .swt-out *, .swt-out *::before, .swt-out *::after { animation: none !important; }
          /* Rings are invisible at their keyframe start, so freezing them is not enough:
             without this the corridor would simply not be drawn. */
          .swt-out-ring { transform: scale(var(--rest)); opacity: .5; }
        }
      `}</style>

      <div className="swt-out absolute inset-0">
        {/* 1. The shaft, still coming toward the camera — you leave a mine by walking
               forward up it, not by reversing back down. Same authored arc as the
               playable view and the entrance, so all three are one place. */}
        <svg
          aria-hidden
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            /* The corridor washes out as the day takes over — the walls stop being
               the subject well before the frame does. */
            opacity: phase === 'walking' ? 1 : 0.25,
            transition: `opacity ${BLOOM_MS}ms ease-out`,
          }}
        >
          {Array.from({ length: RING_COUNT }, (_, i) => {
            const rest = 0.22 + (i / RING_COUNT) * 1.5;
            return (
              <g
                key={i}
                className="swt-out-ring"
                style={{
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  ['--rest' as string]: String(rest),
                  animation:
                    `swtOutRing ${RING_CYCLE_S}s linear ${(i * RING_CYCLE_S) / RING_COUNT}s infinite`,
                }}
              >
                <g transform="translate(0 -4)">
                  <path
                    d={arc(100, 100)}
                    fill="var(--cave-wall)"
                    fillOpacity={0.42}
                    stroke="var(--cave-rim)"
                    strokeWidth={0.4}
                    strokeOpacity={0.45}
                  />
                  <rect x="35" y="93" width="30" height="1.8" rx="0.5"
                        fill="#2A1E14" fillOpacity="0.8" />
                </g>
              </g>
            );
          })}
        </svg>

        {/*
          2. The mouth, and the whole point of the shot.

          A Cycles render (`tools/blender/build_cave_exit.py`) rather than a CSS disc:
          the beat is "there is a shape of rock around the light, and you are inside
          it", and a soft circular gradient says *loading* where a torn stone edge with
          daylight spilling round it says *the way out*. The edge is the picture; the
          light is only what makes the edge visible.

          It grows rather than fades, because the mouth gets nearer — you walk out of a
          mine, you do not dissolve into the daylight.

          Opaque on purpose. Every other sheet in `assets/cave/renders` has a
          transparent middle; this one composites UNDER the bloom, so alpha here would
          show the near-black corridor through the one part of frame that is the sun.
        */}
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/cave-mouth-out.webp)',
            transform: `scale(${1 + (day - 1) * 0.42})`,
            transformOrigin: '50% 52%',
            transition: `transform ${BLOOM_MS + 420}ms cubic-bezier(.4,0,.2,1)`,
          }}
        />

        {/* 2b. The day breathing through the opening. Small, and centred on the hole
               in the render rather than on the frame — it is there to make the light
               alive, not to be the light. */}
        <div
          aria-hidden
          className="absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            width: '34vmin',
            height: '24vmin',
            transform: `translate(-50%,-50%) scale(${day})`,
            transition: `transform ${BLOOM_MS + 260}ms cubic-bezier(.4,0,.2,1)`,
            background:
              'radial-gradient(ellipse, rgba(255,250,232,.62) 0%, rgba(255,232,176,.34) 42%,'
              + ' rgba(255,196,110,0) 74%)',
            animation: `swtOutDay ${BEAT_S * 3}s ease-in-out infinite`,
          }}
        />

        {/*
          3. The miner, backlit.

          A silhouette rather than the drawn character: at the mouth the light is in
          front of them and the torch is doing nothing, so the honest picture is an
          outline with a rim of day on it. It is the only moment in the game the player
          sees their own character from outside rather than from behind.
        */}
        <div
          aria-hidden
          className="absolute bottom-[19%] left-1/2 h-[20%] w-[12%]"
          style={{
            animation: `swtOutWalk ${BEAT_S}s ease-in-out infinite`,
            opacity: phase === 'walking' ? 0.92 : 0,
            transition: `opacity ${BLOOM_MS}ms ease-out`,
          }}
        >
          <svg viewBox="0 0 40 70" className="h-full w-full" preserveAspectRatio="xMidYMax meet">
            <g fill="#120C08">
              <circle cx="20" cy="12" r="9" />
              <rect x="11" y="22" width="18" height="26" rx="4" />
              <rect x="13" y="47" width="5.5" height="20" rx="2.4" />
              <rect x="21.5" y="47" width="5.5" height="20" rx="2.4" />
            </g>
            {/* The rim. Without it the silhouette is a black blob on a bright field —
                the inverse of the mistake this cave usually makes, and just as unread. */}
            <g fill="none" stroke="rgba(255,236,190,.85)" strokeWidth="1.2" strokeLinecap="round">
              <path d="M11.6 9.5A9 9 0 0 1 20 3" />
              <path d="M11.4 24.5v22" />
            </g>
          </svg>
        </div>

        {/* 4. The white-out. Covers the handoff to the card so the corridor does not
               have to finish resolving on screen. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[#FFF6E2]"
          style={{
            opacity: phase === 'walking' ? 0 : 0.9,
            transition: `opacity ${BLOOM_MS}ms ease-in`,
          }}
        />
      </div>

      {phase === 'summary' && summary && (
        <RunSummaryCard summary={summary} onDone={() => onDoneRef.current()} />
      )}
    </div>
  );
}

/**
 * What the descent came to.
 *
 * Deliberately not auto-dismissed. Every other card in the cave clears itself because
 * it is a beat inside a run; this one *is* the end of the session, and a haul that
 * disappears before it can be read is the same silence the exit used to have.
 *
 * Kyat is shown only when there is some. It is withdrawable, and a row reading
 * "0.00 Shwe Kyat" on every ordinary run teaches players to stop reading the line
 * that matters on the run where it is not zero.
 */
export function RunSummaryCard({ summary, onDone }: {
  summary: RunSummaryDTO; onDone: () => void;
}) {
  const newBest = summary.depth_m > 0 && summary.depth_m >= summary.best_depth_m;
  const kyat = Number(summary.kyat);

  return (
    <div
      className="absolute inset-0 grid place-items-center p-5 backdrop-blur-sm"
      /*
       * Inline, and warm.
       *
       * It was `bg-[#0B0705]/88` — a dark scrim, and one Tailwind never emitted: an
       * arbitrary hex with an opacity modifier produced no rule at all, so the card
       * floated on the raw bloom with no backdrop. It looked fine, which is exactly
       * why it would have shipped. Written as a style so it cannot silently not exist.
       *
       * And it is a warm veil rather than the dark one now, because the fiction is
       * that the player has just walked out into the daylight. Darkening the frame
       * again at the one moment they are finally outside would undo the whole shot.
       */
      style={{ background: 'rgba(255,246,226,.62)' }}
    >
      <div
        className="w-full max-w-[380px] rounded-lg border border-ui-lit
                   bg-[linear-gradient(#2A1B10,#160E08)] p-4
                   shadow-[0_18px_54px_-18px_rgba(0,0,0,.9)]"
        style={{ animation: 'swtCard 320ms ease-out both' }}
      >
        <div className="flex items-center gap-2.5">
          <span className="grid h-10 w-10 place-items-center rounded-xl border border-ui-lit
                           bg-[linear-gradient(#3B2917,#1D1309)] text-torch-core" aria-hidden>
            <Icon name="home" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-pixel text-[9px] tracking-wide text-torch-core">BACK AT THE SURFACE</p>
            <p className="text-[11px] text-ink-dim">
              {readableDuration(summary.duration_s)} underground
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <Cell icon="flag" label="Depth" value={`${summary.depth_m}m`} />
          <Cell icon="rock" label="Rocks broken" value={summary.rocks.toLocaleString('en-US')} />
          <Cell icon="coin" label="Shwe Coins" value={summary.coins.toLocaleString('en-US')} tone="coin" />
          {kyat > 0
            ? <Cell icon="kyat" label="Shwe Kyat" value={summary.kyat} tone="kyat" />
            : <Cell icon="trophy" label="Best depth" value={`${summary.best_depth_m}m`} />}
        </div>

        {newBest && (
          <p className="mt-2.5 flex items-center justify-center gap-1.5 rounded-md
                        border border-[rgba(245,197,66,.34)] bg-[rgba(245,197,66,.10)]
                        py-1.5 font-pixel text-[7px] tracking-wide text-coin">
            <Icon name="trophy" size={12} aria-hidden />
            DEEPEST YET · {summary.best_depth_m}m
          </p>
        )}

        <button
          type="button"
          onClick={onDone}
          autoFocus
          className="mt-3 w-full rounded-sm border border-[rgba(255,220,170,.5)]
                     bg-[linear-gradient(#FFA945,#E0761C)] py-3 font-extrabold text-[#2A1503]
                     shadow-[0_4px_0_-1px_#8C440C] active:translate-y-0.5"
        >
          Sell your haul
        </button>
      </div>
    </div>
  );
}

function Cell({ icon, label, value, tone }: {
  icon: 'flag' | 'rock' | 'coin' | 'kyat' | 'trophy';
  label: string;
  value: string;
  tone?: 'coin' | 'kyat';
}) {
  const colour = tone === 'coin' ? 'text-coin' : tone === 'kyat' ? 'text-kyat-glow' : 'text-ink-hi';
  return (
    <div className="rounded-lg bg-black/35 px-2.5 py-2">
      <p className="flex items-center gap-1.5 text-[10px] text-ink-dim">
        <Icon name={icon} size={11} aria-hidden />
        {label}
      </p>
      <b className={`mt-0.5 block tabular text-[15px] ${colour}`}>{value}</b>
    </div>
  );
}
